// Tema de cor da marca parceira no Portal do Consumidor (#6117). Puro — gera o CSS.
//
// O Tailwind vem por CDN com `primary` fixo em index.html (#5F6C37, verde da Vigor), e as
// telas do portal usam ~30 variações (bg-primary, hover:bg-primary/90, ring-primary/20…).
// Em vez de trocar classe por classe em cada tela, o portal põe `marca-<slug>` no <body>
// e este CSS repinta essas classes. No <body> e não num wrapper porque os modais do portal
// abrem por portal, fora da árvore da página.
//
// Ao usar uma variação nova de `primary` no portal, inclua em VARIACOES.

export interface CoresMarca {
  /** Cor principal: botões, links, destaques. Precisa de contraste ≥ 4.5 com branco. */
  primaria: string;
  /** Fim do degradê do painel de login. */
  primariaEscura: string;
}

/** Laranja da Gran alguns tons abaixo do logo (#F28C38): é o mais claro em que o texto
 *  branco dos botões passa de 4.5:1 (#C2571A dava 4.49 — ver o teste de contraste). */
export const CORES_GRAN: CoresMarca = { primaria: '#B85418', primariaEscura: '#7A3410' };

type Prop = 'background-color' | 'color' | 'border-color' | '--tw-ring-color' | '--tw-shadow-color' | '--tw-gradient-from' | '--tw-gradient-to';

interface Variacao { classe: string; prop: Prop; alfa?: number; pseudo?: string; escura?: boolean }

const V = (classe: string, prop: Prop, alfa?: number, pseudo?: string, escura?: boolean): Variacao => ({ classe, prop, alfa, pseudo, escura });

/** Variações de `primary` usadas nas telas do portal (levantadas em 27/09/2026). */
export const VARIACOES: Variacao[] = [
  V('bg-primary', 'background-color'),
  V('bg-primary/5', 'background-color', 0.05),
  V('bg-primary/10', 'background-color', 0.1),
  V('bg-primary-dark/95', 'background-color', 0.95, undefined, true),
  V('hover:bg-primary/90', 'background-color', 0.9, ':hover'),
  V('hover:bg-primary/5', 'background-color', 0.05, ':hover'),
  V('hover:bg-primary/10', 'background-color', 0.1, ':hover'),
  V('hover:bg-primary/20', 'background-color', 0.2, ':hover'),
  V('text-primary', 'color'),
  V('hover:text-primary', 'color', undefined, ':hover'),
  V('hover:text-primary/80', 'color', 0.8, ':hover'),
  V('hover:text-primary/70', 'color', 0.7, ':hover'),
  V('group-hover:text-primary', 'color', undefined, 'group'),
  V('border-primary', 'border-color'),
  V('border-primary/15', 'border-color', 0.15),
  V('border-primary/20', 'border-color', 0.2),
  V('border-primary/30', 'border-color', 0.3),
  V('border-primary/40', 'border-color', 0.4),
  V('hover:border-primary/30', 'border-color', 0.3, ':hover'),
  V('hover:border-primary/40', 'border-color', 0.4, ':hover'),
  V('focus:border-primary', 'border-color', undefined, ':focus'),
  V('focus:ring-primary', '--tw-ring-color', undefined, ':focus'),
  V('focus:ring-primary/20', '--tw-ring-color', 0.2, ':focus'),
  V('focus:ring-primary/30', '--tw-ring-color', 0.3, ':focus'),
  V('focus-visible:ring-primary/40', '--tw-ring-color', 0.4, ':focus-visible'),
  V('shadow-primary/25', '--tw-shadow-color', 0.25),
  V('from-primary', '--tw-gradient-from'),
  V('to-primary-dark', '--tw-gradient-to', undefined, undefined, true),
];

/** '#B85418' + alfa → 'rgb(184 84 24 / 0.1)'. */
export function corComAlfa(hex: string, alfa?: number): string {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
  return alfa === undefined ? `rgb(${r} ${g} ${b})` : `rgb(${r} ${g} ${b} / ${alfa})`;
}

/** Escapa o nome da classe do Tailwind para seletor CSS (`/` e `:`). */
export const escaparClasse = (c: string) => c.replace(/([:/.])/g, '\\$1');

export function cssTemaMarca(slug: string, cores: CoresMarca): string {
  const raiz = `body.marca-${slug}`;
  return VARIACOES.map(v => {
    const cor = corComAlfa(v.escura ? cores.primariaEscura : cores.primaria, v.alfa);
    const sel = `.${escaparClasse(v.classe)}`;
    const seletor = v.pseudo === 'group' ? `${raiz} .group:hover ${sel}` : `${raiz} ${sel}${v.pseudo ?? ''}`;
    // Degradê do Tailwind: o `from` também define o `to` padrão e a pilha de cores.
    const valor = v.prop === '--tw-gradient-from'
      ? `--tw-gradient-from: ${cor}; --tw-gradient-to: rgb(255 255 255 / 0); --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);`
      : `${v.prop}: ${cor};`;
    return `${seletor} { ${valor} }`;
  }).join('\n');
}
